import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AutoAwesome as AutoAwesomeIcon,
  Language as LanguageIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { useSearchParams } from 'react-router-dom';
import { buildIndustrySearchQueriesFromEnrichment, scoreIndustryResultFromEnrichment } from '../utils/industrySuggestion';
import { 
  companyAPI, 
  referenceAPI,
  industryAPI,
  userAPI,
  kpiAPI,
  Company, 
  CompanySite, 
  CompanyUser,
  User,
  Country, 
  GICS,
  IndustryLevel01,
  IndustryLevel02,
  IndustryLevel03,
  IndustryLevel04,
  CompanyEnrichmentResponse,
  IndustrySearchResult,
  KPI
} from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CompanyManagement: React.FC = () => {
  const { user } = useAuth();
  const { userPermissions, refreshCompanies } = useCompany();
  const [searchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(0);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [highlightedCompanyId, setHighlightedCompanyId] = useState<number | null>(null);
  
  // Reference data
  const [countries, setCountries] = useState<Country[]>([]);
  const [gics, setGics] = useState<GICS[]>([]);
  
  // Industry data
  const [level01Industries, setLevel01Industries] = useState<IndustryLevel01[]>([]);
  const [level02Industries, setLevel02Industries] = useState<IndustryLevel02[]>([]);
  const [level03Industries, setLevel03Industries] = useState<IndustryLevel03[]>([]);
  const [level04Industries, setLevel04Industries] = useState<IndustryLevel04[]>([]);

  // Industry search (Level 4 search with full hierarchy returned)
  const [industrySearchQuery, setIndustrySearchQuery] = useState<string>('');
  const [industrySearchValue, setIndustrySearchValue] = useState<IndustrySearchResult | null>(null);
  const [industrySearchOptions, setIndustrySearchOptions] = useState<IndustrySearchResult[]>([]);
  const [industrySearchLoading, setIndustrySearchLoading] = useState<boolean>(false);
  const [industryAutoSelectedMessage, setIndustryAutoSelectedMessage] = useState<string | null>(null);
  
  // Dialog states
  const [companyDialog, setCompanyDialog] = useState(false);
  const [siteDialog, setSiteDialog] = useState(false);
  const [userAssignDialog, setUserAssignDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [editingSite, setEditingSite] = useState<CompanySite | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  
  // Sites and users for selected company
  const [companySites, setCompanySites] = useState<CompanySite[]>([]);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  
  // All users for assignment
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isPrimaryUser, setIsPrimaryUser] = useState(false);
  
  // Add vs Assign mode
  const [isAddMode, setIsAddMode] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
  });
  
  // Form validation errors
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // Form data
  const [companyForm, setCompanyForm] = useState({
    name: '',
    postcode: '',
    city: '',
    country: '',
    employee_count: 0,
    sub_industry: '', // Legacy field
    industry_level01: '',
    industry_level02: '',
    industry_level03: '',
    industry_level04: '',
    site_count: 0,
    website_url: '',
  });
  
  // Company enrichment state
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  const [enrichmentResult, setEnrichmentResult] = useState<CompanyEnrichmentResponse | null>(null);
  const [enrichmentError, setEnrichmentError] = useState<string | null>(null);
  const [showFullProfile, setShowFullProfile] = useState(false);

  // KPI management (embedded in Company create/edit modal)
  const [kpiSectionExpanded, setKpiSectionExpanded] = useState(true);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiError, setKpiError] = useState<string | null>(null);
  const [isGeneratingKpis, setIsGeneratingKpis] = useState(false);
  const [companyKpis, setCompanyKpis] = useState<KPI[]>([]);
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [editingKpi, setEditingKpi] = useState<KPI | null>(null);
  const [kpiForm, setKpiForm] = useState<{
    name: string;
    description: string;
    unit_type: 'percentage' | 'currency' | 'number' | 'days' | 'count';
    target_value: string;
    tolerance_percentage: string;
    higher_is_better: boolean;
  }>({
    name: '',
    description: '',
    unit_type: 'number',
    target_value: '',
    tolerance_percentage: '10',
    higher_is_better: true,
  });
  
  const [siteForm, setSiteForm] = useState({
    name: '',
    engineers: false,
    manufacturers_internal: false,
    manufacturers_outsourced: false,
    purchases: false,
    holds_inventory: false,
    raw_material_part_count: 0,
    finished_good_part_count: 0,
  });

  // Check if user can manage clients (multi-tenant permissions)
  const canManageClients = userPermissions?.can_manage_clients || userPermissions?.is_super_admin;

  // Handle highlight parameter from URL
  useEffect(() => {
    const highlightParam = searchParams.get('highlight');
    if (highlightParam && companies.length > 0) {
      const companyId = parseInt(highlightParam);
      setHighlightedCompanyId(companyId);
      console.log('✅ Highlighting company ID:', companyId);
      
      // Find and auto-open edit dialog for the highlighted company
      const targetCompany = companies.find(c => c.id === companyId);
      if (targetCompany) {
        console.log('✅ Auto-opening edit dialog for:', targetCompany.name);
        setTimeout(() => {
          openCompanyDialog(targetCompany);
        }, 500); // Small delay to let the UI render
      }
      
      // Clear the highlight after 5 seconds
      const timer = setTimeout(() => {
        setHighlightedCompanyId(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [searchParams, companies]);

  useEffect(() => {
    // Wait for userPermissions to load before checking permissions
    if (userPermissions === null) {
      return; // Still loading permissions
    }
    
    if (!canManageClients) {
      setError('Client management access required');
      return;
    }
    loadData();
  }, [canManageClients, userPermissions]);

  // Set Australia as default country when countries are loaded
  useEffect(() => {
    if (countries.length > 0 && companyForm.country === '') {
      const australia = countries.find(c => c.name === 'Australia');
      if (australia) {
        setCompanyForm(prev => ({ ...prev, country: australia.id.toString() }));
      }
    }
  }, [countries, companyForm.country]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [companiesResponse, countriesData, gicsData, level01Data] = await Promise.all([
        companyAPI.getCompanies(),
        referenceAPI.getCountries(),
        referenceAPI.getGICS(),
        industryAPI.getLevel01Industries(),
      ]);
      
      // Extract all client companies from the multi-tenant response
      // Deduplicate by company ID to avoid showing the same company twice
      const clientsMap = new Map<number, Company>();
      
      // Add direct client companies
      companiesResponse.client_companies.forEach(company => {
        clientsMap.set(company.id, company);
      });
      
      // Add client companies from consultant companies
      companiesResponse.consultant_companies.forEach(consultant => {
        if (consultant.client_companies) {
          consultant.client_companies.forEach(company => {
            if (!clientsMap.has(company.id)) {
              clientsMap.set(company.id, company);
            }
          });
        }
      });
      
      setCompanies(Array.from(clientsMap.values()));
      setCountries(countriesData);
      setGics(gicsData);
      setLevel01Industries(level01Data);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyDetails = async (company: Company) => {
    try {
      setSelectedCompany(company);
      const [sitesData, usersData] = await Promise.all([
        companyAPI.getCompanySites(company.id),
        companyAPI.getCompanyUsers(company.id),
      ]);
      setCompanySites(sitesData);
      setCompanyUsers(usersData);
    } catch (error) {
      console.error('Error loading company details:', error);
      setError('Failed to load company details');
    }
  };

  const loadAllUsers = async () => {
    try {
      const usersData = await userAPI.getUnassignedUsers();
      setAllUsers(usersData);
      console.log(`✅ Loaded ${usersData.length} unassigned users`);
    } catch (error) {
      console.error('❌ Error loading users:', error);
      setError('Failed to load users list');
    }
  };

  const handleAssignUser = async () => {
    if (!selectedCompany || !selectedUserId) {
      setError('Please select a user to assign');
      return;
    }

    try {
      await companyAPI.assignUserToCompany(selectedCompany.id, selectedUserId, isPrimaryUser);
      
      // Reload company users to show the new assignment
      const usersData = await companyAPI.getCompanyUsers(selectedCompany.id);
      setCompanyUsers(usersData);
      
      // Reload unassigned users list (to remove the just-assigned user)
      await loadAllUsers();
      
      // Close dialog and reset form
      setUserAssignDialog(false);
      setSelectedUserId(null);
      setIsPrimaryUser(false);
      
      console.log(`✅ User assigned to ${selectedCompany.name}`);
    } catch (error) {
      console.error('❌ Error assigning user:', error);
      setError('Failed to assign user to company');
    }
  };

  const handleCreateAndAssignUser = async () => {
    if (!selectedCompany) {
      setError('No company selected');
      return;
    }

    // Validate form
    if (!newUserForm.email || !newUserForm.first_name || !newUserForm.last_name) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      // Invite the user - they'll receive an email to set their password
      const newUser = await userAPI.createUser({
        email: newUserForm.email,
        first_name: newUserForm.first_name,
        last_name: newUserForm.last_name,
      });

      // Then assign the user to the company
      await companyAPI.assignUserToCompany(selectedCompany.id, newUser.id, isPrimaryUser);
      
      // Reload company users to show the new assignment
      const usersData = await companyAPI.getCompanyUsers(selectedCompany.id);
      setCompanyUsers(usersData);
      
      // Reload unassigned users list (new user is now assigned)
      await loadAllUsers();
      
      // Close dialog and reset form
      setUserAssignDialog(false);
      resetUserForm();
      
      console.log(`✅ New user created and assigned to ${selectedCompany.name}`);
    } catch (error) {
      console.error('❌ Error creating and assigning user:', error);
      setError('Failed to create and assign user');
    }
  };

  const resetUserForm = () => {
    setSelectedUserId(null);
    setIsPrimaryUser(false);
    setIsAddMode(false);
    setNewUserForm({
      email: '',
      first_name: '',
      last_name: '',
    });
  };

  const openUserAssignDialog = () => {
    if (allUsers.length === 0) {
      loadAllUsers();
    }
    setUserAssignDialog(true);
  };

  /**
   * Fetch company information using AI enrichment
   * Requires company name, website URL is optional but recommended
   */
  const handleEnrichCompany = async () => {
    // Validate that we have a company name
    if (!companyForm.name.trim()) {
      setEnrichmentError('Please enter a company name first');
      return;
    }
    
    // If research already exists, confirm before refreshing
    if (editingCompany?.company_research || enrichmentResult) {
      const confirmed = window.confirm(
        'Company research already exists. Are you sure you want to refresh it with new data?'
      );
      if (!confirmed) {
        return;
      }
    }
    
    setEnrichmentLoading(true);
    setEnrichmentError(null);
    setEnrichmentResult(null);
    setIndustryAutoSelectedMessage(null);
    
    try {
      // If we are editing an existing company, pass the company_id so the
      // backend saves the research to the DB and triggers background KPI generation.
      const result = await companyAPI.enrichCompany(
        companyForm.name,
        companyForm.website_url || undefined,
        editingCompany?.id
      );
      
      console.log('[Enrichment] Received result for:', result.company_name);
      console.log('[Enrichment] Structured data:', result.structured_data);
      
      setEnrichmentResult(result);
      
      // Keep local company state in sync so the UI immediately reflects that
      // research is now available (and persisted, if editingCompany exists).
      if (editingCompany) {
        setEditingCompany(prev => (prev ? { ...prev, company_research: result } : prev));
        setCompanies(prev => prev.map(c => (c.id === editingCompany.id ? { ...c, company_research: result } : c)));
        // Research save triggers background KPI generation on the backend; start polling
        // so the KPI Management section updates automatically when KPIs appear.
        startKpiPolling(editingCompany.id);
      }
      
      // Auto-populate form fields from structured data if available
      if (result.structured_data) {
        const data = result.structured_data;
        
        // Auto-fill city if empty and we have headquarters_city
        if (!companyForm.city && data.headquarters_city) {
          setCompanyForm(prev => ({ ...prev, city: data.headquarters_city || '' }));
        }
        
        // Auto-fill country if we have headquarters_country
        if (data.headquarters_country) {
          const matchedCountry = countries.find(
            c => c.name.toLowerCase() === data.headquarters_country?.toLowerCase()
          );
          if (matchedCountry && !companyForm.country) {
            setCompanyForm(prev => ({ ...prev, country: matchedCountry.id.toString() }));
          }
        }
        
        // Auto-fill employee count if empty and we have an estimate
        if (!companyForm.employee_count && data.employee_count_estimate) {
          // Parse the estimate - handle formats like "200", "200-500", "~500"
          const estimate = data.employee_count_estimate.toString();
          const numMatch = estimate.match(/\d+/);
          if (numMatch) {
            setCompanyForm(prev => ({ ...prev, employee_count: parseInt(numMatch[0]) }));
          }
        }
      }

      // Suggest industry selection from enrichment (only if not already selected)
      void autoSelectIndustryFromEnrichment(result);
      
    } catch (err) {
      console.error('Error enriching company:', err);
      setEnrichmentError('Failed to fetch company information. Please try again.');
    } finally {
      setEnrichmentLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // KPI Management (embedded in Company create/edit modal)
  // ---------------------------------------------------------------------------

  const resetKpiState = () => {
    setKpiLoading(false);
    setKpiError(null);
    setIsGeneratingKpis(false);
    setCompanyKpis([]);
    setKpiDialogOpen(false);
    setEditingKpi(null);
    setKpiForm({
      name: '',
      description: '',
      unit_type: 'number',
      target_value: '',
      tolerance_percentage: '10',
      higher_is_better: true,
    });
  };

  const loadCompanyKpis = async (companyId: number) => {
    try {
      setKpiLoading(true);
      setKpiError(null);
      const data = await kpiAPI.getCompanyKPIs(companyId);
      setCompanyKpis(data.kpis || []);
    } catch (err: any) {
      console.error('Failed to load KPIs:', err);
      setKpiError(err?.message || 'Failed to load KPIs');
    } finally {
      setKpiLoading(false);
    }
  };

  const startKpiPolling = (companyId: number) => {
    setIsGeneratingKpis(true);
    setKpiError(null);

    let attempts = 0;
    const maxAttempts = 24; // ~2 minutes
    const pollIntervalMs = 5000;

    const check = async () => {
      attempts += 1;
      try {
        const data = await kpiAPI.getCompanyKPIs(companyId);
        const kpis = data.kpis || [];
        setCompanyKpis(kpis);

        if (kpis.length > 0) {
          setIsGeneratingKpis(false);
          return;
        }
      } catch (err) {
        // Non-fatal: continue polling (generation can take time)
        console.warn('KPI poll attempt failed:', err);
      }

      if (attempts < maxAttempts) {
        window.setTimeout(check, pollIntervalMs);
      } else {
        setIsGeneratingKpis(false);
      }
    };

    window.setTimeout(check, pollIntervalMs);
  };

  const handleGenerateKPIs = async () => {
    if (!editingCompany?.id) return;
    try {
      setIsGeneratingKpis(true);
      setKpiError(null);
      await kpiAPI.generateKPIsForCompany(editingCompany.id);
      startKpiPolling(editingCompany.id);
    } catch (err: any) {
      console.error('Failed to start KPI generation:', err);
      setKpiError(err?.message || 'Failed to start KPI generation');
      setIsGeneratingKpis(false);
    }
  };

  const openCreateKpiDialog = () => {
    setEditingKpi(null);
    setKpiForm({
      name: '',
      description: '',
      unit_type: 'number',
      target_value: '',
      tolerance_percentage: '10',
      higher_is_better: true,
    });
    setKpiDialogOpen(true);
  };

  const openEditKpiDialog = (kpi: KPI) => {
    setEditingKpi(kpi);
    setKpiForm({
      name: kpi.name,
      description: kpi.description || '',
      unit_type: kpi.unit_type,
      target_value: kpi.target_value,
      tolerance_percentage: kpi.tolerance_percentage,
      higher_is_better: kpi.higher_is_better,
    });
    setKpiDialogOpen(true);
  };

  const handleSaveKpi = async () => {
    if (!editingCompany?.id) return;
    try {
      setKpiError(null);
      if (editingKpi) {
        await kpiAPI.updateKPI(editingKpi.id, kpiForm);
      } else {
        await kpiAPI.createKPI(editingCompany.id, kpiForm);
      }
      setKpiDialogOpen(false);
      setEditingKpi(null);
      await loadCompanyKpis(editingCompany.id);
    } catch (err: any) {
      console.error('Failed to save KPI:', err);
      setKpiError(err?.message || 'Failed to save KPI');
    }
  };

  const handleDeleteKpi = async (kpi: KPI) => {
    if (!window.confirm(`Are you sure you want to delete "${kpi.name}"? This will also delete all historical data for this KPI.`)) {
      return;
    }
    try {
      setKpiError(null);
      await kpiAPI.deleteKPI(kpi.id);
      if (editingCompany?.id) {
        await loadCompanyKpis(editingCompany.id);
      }
    } catch (err: any) {
      console.error('Failed to delete KPI:', err);
      setKpiError(err?.message || 'Failed to delete KPI');
    }
  };

  const handleCreateCompany = async () => {
    try {
      // Format website URL - add https:// if missing protocol
      let websiteUrl = companyForm.website_url?.trim() || undefined;
      if (websiteUrl && !websiteUrl.match(/^https?:\/\//i)) {
        websiteUrl = `https://${websiteUrl}`;
      }
      
      const companyData = {
        name: companyForm.name,
        postcode: companyForm.postcode,
        city: companyForm.city,
        country: parseInt(companyForm.country),
        employee_count: companyForm.employee_count,
        site_count: companyForm.site_count,
        website_url: websiteUrl,
        // If the user fetched company research during creation, include it so the backend
        // can persist it and trigger KPI generation without re-calling the LLM.
        ...(enrichmentResult ? { company_research: enrichmentResult } : {}),
        // Use new industry field if available, fallback to legacy
        ...(companyForm.industry_level04 ? 
          { industry_level04: parseInt(companyForm.industry_level04) } : 
          { sub_industry: parseInt(companyForm.sub_industry) }
        )
      };
      const newCompany = await companyAPI.createCompany(companyData);
      setCompanies([...companies, newCompany]);
      
      // Refresh the company list in context to update other pages
      await refreshCompanies();
      
      // Keep the modal open and switch into "edit" mode so KPIs can be managed
      // immediately (KPIs require a persisted company_id).
      setEditingCompany(newCompany);
      setFormErrors({});
      
      // Clear enrichment state since it's now persisted in the DB
      // This prevents it from bleeding into the next "Create New Company" dialog
      setEnrichmentResult(null);
      setEnrichmentError(null);
      setShowFullProfile(false);
      setIndustryAutoSelectedMessage(null);
      
      resetKpiState();
      setKpiSectionExpanded(true);

      // Load KPIs and (if research was provided) start polling while background
      // KPI generation runs.
      await loadCompanyKpis(newCompany.id);
      if (newCompany.company_research) {
        startKpiPolling(newCompany.id);
      }
    } catch (error) {
      console.error('Error creating company:', error);
      setError('Failed to create company');
    }
  };

  const handleUpdateCompany = async () => {
    if (!editingCompany) return;
    
    try {
      // Format website URL - add https:// if missing protocol
      let websiteUrl = companyForm.website_url?.trim() || undefined;
      if (websiteUrl && !websiteUrl.match(/^https?:\/\//i)) {
        websiteUrl = `https://${websiteUrl}`;
      }
      
      const companyData = {
        name: companyForm.name,
        postcode: companyForm.postcode,
        city: companyForm.city,
        country: parseInt(companyForm.country),
        employee_count: companyForm.employee_count,
        site_count: companyForm.site_count,
        website_url: websiteUrl,
        // Use new industry field if available, fallback to legacy
        ...(companyForm.industry_level04 ? 
          { industry_level04: parseInt(companyForm.industry_level04) } : 
          { sub_industry: parseInt(companyForm.sub_industry) }
        )
      };
      const updatedCompany = await companyAPI.updateCompany(editingCompany.id, companyData);
      setCompanies(companies.map(c => c.id === editingCompany.id ? updatedCompany : c));
      
      // Refresh the company list in context to update other pages
      await refreshCompanies();
      
      setCompanyDialog(false);
      resetCompanyForm();
      setFormErrors({});
    } catch (error) {
      console.error('Error updating company:', error);
      setError('Failed to update company');
    }
  };

  const handleDeleteCompany = async (company: Company) => {
    if (!window.confirm(`Are you sure you want to delete ${company.name}?`)) return;
    
    try {
      await companyAPI.deleteCompany(company.id);
      setCompanies(companies.filter(c => c.id !== company.id));
    } catch (error) {
      console.error('Error deleting company:', error);
      setError('Failed to delete company');
    }
  };

  const handleCreateSite = async () => {
    if (!selectedCompany) return;
    
    // Validate site name
    if (!siteForm.name.trim()) {
      setError('Site name is required');
      return;
    }
    
    try {
      const newSite = await companyAPI.createCompanySite(selectedCompany.id, siteForm);
      setCompanySites([...companySites, newSite]);
      setSiteDialog(false);
      resetSiteForm();
    } catch (error) {
      console.error('Error creating site:', error);
      setError('Failed to create site');
    }
  };

  const resetCompanyForm = () => {
    // Find Australia's ID (should be 13 based on our load script)
    const australia = countries.find(c => c.name === 'Australia');
    const australiaId = australia ? australia.id.toString() : '';
    
    setCompanyForm({
      name: '',
      postcode: '',
      city: '',
      country: australiaId,
      employee_count: 0,
      sub_industry: '',
      industry_level01: '',
      industry_level02: '',
      industry_level03: '',
      industry_level04: '',
      site_count: 0,
      website_url: '',
    });
    setEditingCompany(null);
    setFormErrors({});
    
    // Reset dependent industry dropdowns
    setLevel02Industries([]);
    setLevel03Industries([]);
    setLevel04Industries([]);
    
    // Reset enrichment state
    setEnrichmentResult(null);
    setEnrichmentError(null);
    setShowFullProfile(false);
  };

  const validateCompanyForm = () => {
    const errors: Record<string, string> = {};
    
    if (!companyForm.name.trim()) {
      errors.name = 'Company name is required';
    }
    
    if (!companyForm.postcode.trim()) {
      errors.postcode = 'Postcode is required';
    }
    
    if (!companyForm.city.trim()) {
      errors.city = 'City is required';
    }
    
    if (!companyForm.country || companyForm.country === '' || parseInt(companyForm.country) <= 0) {
      errors.country = 'Please select a country';
    }
    
    if (!companyForm.industry_level01 || companyForm.industry_level01 === '') {
      errors.industry_level01 = 'Please select primary industry';
    }
    
    if (!companyForm.industry_level02 || companyForm.industry_level02 === '') {
      errors.industry_level02 = 'Please select sector';
    }
    
    if (!companyForm.industry_level03 || companyForm.industry_level03 === '') {
      errors.industry_level03 = 'Please select subsector';
    }
    
    if (!companyForm.industry_level04 || companyForm.industry_level04 === '') {
      errors.industry_level04 = 'Please select specific industry';
    }
    
    if (companyForm.employee_count <= 0) {
      errors.employee_count = 'Employee count must be greater than 0';
    }
    
    if (companyForm.site_count <= 0) {
      errors.site_count = 'Site count must be greater than 0';
    }
    
    return errors;
  };

  const isFormValid = () => {
    return (
      companyForm.name.trim() !== '' &&
      companyForm.postcode.trim() !== '' &&
      companyForm.city.trim() !== '' &&
      companyForm.country !== '' &&
      !isNaN(parseInt(companyForm.country)) &&
      parseInt(companyForm.country) > 0 &&
      companyForm.industry_level04 !== '' &&
      !isNaN(parseInt(companyForm.industry_level04)) &&
      parseInt(companyForm.industry_level04) > 0 &&
      companyForm.employee_count > 0 &&
      companyForm.site_count > 0
    );
  };

  // Industry cascade handlers
  const handleLevel01Change = async (e: any) => {
    const level01Id = e.target.value;
    
    setCompanyForm({ 
      ...companyForm, 
      industry_level01: level01Id,
      industry_level02: '',
      industry_level03: '',
      industry_level04: ''
    });
    
    // Clear dependent levels
    setLevel02Industries([]);
    setLevel03Industries([]);
    setLevel04Industries([]);
    
    // Load Level 2 industries
    if (level01Id) {
      try {
        const level02Data = await industryAPI.getLevel02Industries(parseInt(level01Id));
        setLevel02Industries(level02Data);
      } catch (error) {
        console.error('Error loading level 2 industries:', error);
      }
    }
  };

  const handleLevel02Change = async (e: any) => {
    const level02Id = e.target.value;
    setCompanyForm({ 
      ...companyForm, 
      industry_level02: level02Id,
      industry_level03: '',
      industry_level04: ''
    });
    
    // Clear dependent levels
    setLevel03Industries([]);
    setLevel04Industries([]);
    
    // Load Level 3 industries
    if (level02Id) {
      try {
        const level03Data = await industryAPI.getLevel03Industries(parseInt(level02Id));
        setLevel03Industries(level03Data);
      } catch (error) {
        console.error('Error loading level 3 industries:', error);
      }
    }
  };

  const handleLevel03Change = async (e: any) => {
    const level03Id = e.target.value;
    setCompanyForm({ 
      ...companyForm, 
      industry_level03: level03Id,
      industry_level04: ''
    });
    
    // Clear dependent levels
    setLevel04Industries([]);
    
    // Load Level 4 industries
    if (level03Id) {
      try {
        const level04Data = await industryAPI.getLevel04Industries(parseInt(level03Id));
        console.log('[Industry] Loaded Level 04 industries:', level04Data);
        console.log('[Industry] Sample Level 04 detail field:', level04Data[0]?.detail);
        setLevel04Industries(level04Data);
      } catch (error) {
        console.error('Error loading level 4 industries:', error);
      }
    }
  };

  const handleLevel04Change = (e: any) => {
    setCompanyForm({ 
      ...companyForm, 
      industry_level04: e.target.value
    });
  };

  const applyIndustrySearchSelection = async (selection: IndustrySearchResult) => {
    try {
      // Populate the IDs immediately (keeps the form valid)
      setCompanyForm(prev => ({
        ...prev,
        industry_level01: selection.level01.id.toString(),
        industry_level02: selection.level02.id.toString(),
        industry_level03: selection.level03.id.toString(),
        industry_level04: selection.level04.id.toString(),
      }));

      // Clear any existing validation errors
      setFormErrors(prev => ({
        ...prev,
        industry_level01: '',
        industry_level02: '',
        industry_level03: '',
        industry_level04: '',
      }));

      // Ensure dropdown option lists match the selected hierarchy
      const [allLevel01, level02Data, level03Data, level04Data] = await Promise.all([
        level01Industries.length ? Promise.resolve(level01Industries) : industryAPI.getLevel01Industries(),
        industryAPI.getLevel02Industries(selection.level01.id),
        industryAPI.getLevel03Industries(selection.level02.id),
        industryAPI.getLevel04Industries(selection.level03.id),
      ]);

      setLevel01Industries(allLevel01);
      setLevel02Industries(level02Data);
      setLevel03Industries(level03Data);
      setLevel04Industries(level04Data);
    } catch (error) {
      console.error('Failed to apply industry search selection:', error);
    }
  };

  const autoSelectIndustryFromEnrichment = async (result: CompanyEnrichmentResponse) => {
    try {
      console.log('[Industry Auto-Select] Starting with enrichment result:', result.company_name);
      
      // Don't override a selection the user already made.
      if (companyForm.industry_level04) {
        console.log('[Industry Auto-Select] Skipped - industry already selected');
        return;
      }

      const queries = buildIndustrySearchQueriesFromEnrichment(result);
      console.log('[Industry Auto-Select] Generated search queries:', queries);
      
      if (queries.length === 0) {
        console.log('[Industry Auto-Select] No queries generated - skipping');
        return;
      }

      let best: IndustrySearchResult | null = null;
      let bestScore = -1;

      for (const q of queries.slice(0, 5)) {
        console.log(`[Industry Auto-Select] Searching for: "${q}"`);
        const hits = await industryAPI.searchIndustries(q, 20);
        console.log(`[Industry Auto-Select] Found ${hits.length} results for "${q}"`);
        
        for (const hit of hits) {
          const score = scoreIndustryResultFromEnrichment(hit, result);
          if (score > bestScore) {
            bestScore = score;
            best = hit;
            console.log(`[Industry Auto-Select] New best match (score ${score}):`, hit.display);
          }
        }
      }

      // Avoid random/low-confidence picks.
      if (!best || bestScore < 2) {
        console.log(`[Industry Auto-Select] No confident match found (best score: ${bestScore})`);
        return;
      }

      console.log(`[Industry Auto-Select] Applying best match (score ${bestScore}):`, best.display);
      setIndustrySearchValue(best);
      await applyIndustrySearchSelection(best);
      setIndustryAutoSelectedMessage('Industry was suggested from company research. Please review before saving.');
    } catch (error) {
      console.error('[Industry Auto-Select] Failed:', error);
    }
  };

  // Debounced industry search
  useEffect(() => {
    const q = industrySearchQuery.trim();
    if (q.length < 2) {
      setIndustrySearchOptions([]);
      return;
    }

    const handle = window.setTimeout(async () => {
      setIndustrySearchLoading(true);
      try {
        const results = await industryAPI.searchIndustries(q, 50);
        setIndustrySearchOptions(results);
      } catch (error) {
        console.error('Error searching industries:', error);
        setIndustrySearchOptions([]);
      } finally {
        setIndustrySearchLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(handle);
  }, [industrySearchQuery]);

  /**
   * Load the complete industry hierarchy from a Level 04 industry ID
   * This is needed when editing an existing company to populate all dropdown levels
   */
  const loadIndustryHierarchyFromLevel04 = async (level04Id: number) => {
    try {
      console.log('🔍 Loading industry hierarchy from Level 04 ID:', level04Id);
      
      // Load all industries at each level (without filtering)
      const [allLevel01, allLevel02, allLevel03, allLevel04] = await Promise.all([
        industryAPI.getLevel01Industries(),
        industryAPI.getLevel02Industries(),
        industryAPI.getLevel03Industries(),
        industryAPI.getLevel04Industries(),
      ]);
      
      // Find the target Level 04 industry
      const targetLevel04 = allLevel04.find(industry => industry.id === level04Id);
      if (!targetLevel04) {
        console.error('❌ Could not find Level 04 industry:', level04Id);
        return;
      }
      
      // Find the parent Level 03 industry
      const targetLevel03 = allLevel03.find(industry => industry.id === targetLevel04.level03);
      if (!targetLevel03) {
        console.error('❌ Could not find Level 03 industry for Level 04:', targetLevel04.level03);
        return;
      }
      
      // Find the parent Level 02 industry
      const targetLevel02 = allLevel02.find(industry => industry.id === targetLevel03.level02);
      if (!targetLevel02) {
        console.error('❌ Could not find Level 02 industry for Level 03:', targetLevel03.level02);
        return;
      }
      
      // Find the parent Level 01 industry
      const targetLevel01 = allLevel01.find(industry => industry.id === targetLevel02.level01);
      if (!targetLevel01) {
        console.error('❌ Could not find Level 01 industry for Level 02:', targetLevel02.level01);
        return;
      }
      
      console.log('✅ Industry hierarchy loaded:', {
        level01: `${targetLevel01.code} - ${targetLevel01.name}`,
        level02: `${targetLevel02.code} - ${targetLevel02.name}`,
        level03: `${targetLevel03.code} - ${targetLevel03.name}`,
        level04: `${targetLevel04.code} - ${targetLevel04.name}`,
      });
      
      // Set all industries at each level
      setLevel01Industries(allLevel01);
      
      // Filter Level 02 industries to those under the target Level 01
      setLevel02Industries(allLevel02.filter(i => i.level01 === targetLevel01.id));
      
      // Filter Level 03 industries to those under the target Level 02
      setLevel03Industries(allLevel03.filter(i => i.level02 === targetLevel02.id));
      
      // Filter Level 04 industries to those under the target Level 03
      setLevel04Industries(allLevel04.filter(i => i.level03 === targetLevel03.id));
      
      // Update the form with all hierarchy values
      setCompanyForm(prev => ({
        ...prev,
        industry_level01: targetLevel01.id.toString(),
        industry_level02: targetLevel02.id.toString(),
        industry_level03: targetLevel03.id.toString(),
        industry_level04: targetLevel04.id.toString(),
      }));

      // Also sync the search field (helps users see the selected path)
      setIndustrySearchValue({
        level01: { id: targetLevel01.id, code: targetLevel01.code, name: targetLevel01.name },
        level02: { id: targetLevel02.id, code: targetLevel02.code, name: targetLevel02.name },
        level03: { id: targetLevel03.id, code: targetLevel03.code, name: targetLevel03.name },
        level04: { id: targetLevel04.id, code: targetLevel04.code, name: targetLevel04.name },
        display: `${targetLevel01.code} ${targetLevel01.name} > ${targetLevel02.code} ${targetLevel02.name} > ${targetLevel03.code} ${targetLevel03.name} > ${targetLevel04.code} ${targetLevel04.name}`,
      });
      
    } catch (error) {
      console.error('❌ Failed to load industry hierarchy:', error);
    }
  };

  const resetSiteForm = () => {
    setSiteForm({
      name: '',
      engineers: false,
      manufacturers_internal: false,
      manufacturers_outsourced: false,
      purchases: false,
      holds_inventory: false,
      raw_material_part_count: 0,
      finished_good_part_count: 0,
    });
    setEditingSite(null);
  };

  const openCompanyDialog = async (company?: Company) => {
    // CRITICAL: Always reset enrichment state when opening dialog
    setEnrichmentResult(null);
    setEnrichmentError(null);
    setShowFullProfile(false);
    setIndustryAutoSelectedMessage(null);
    resetKpiState();
    
    if (company) {
      setEditingCompany(company);
      // Handle optional fields with proper defaults for client companies
      setCompanyForm({
        name: company.name,
        postcode: company.postcode ?? '',
        city: company.city ?? '',
        country: company.country?.toString() ?? '',
        employee_count: company.employee_count ?? 0,
        sub_industry: company.sub_industry?.toString() ?? '',
        industry_level01: '',
        industry_level02: '',
        industry_level03: '',
        industry_level04: company.industry_level04?.toString() ?? '',
        site_count: company.site_count ?? 0,
        website_url: company.website_url ?? '',
      });
      setFormErrors({});
      
      // Load the complete industry hierarchy if company has an existing classification
      if (company.industry_level04) {
        console.log('🔍 Found existing industry classification, loading hierarchy...');
        await loadIndustryHierarchyFromLevel04(company.industry_level04);
      }
      
      // Load saved company research if available (ONLY for existing companies)
      if (company.company_research) {
        console.log('✅ Loading saved company research for existing company');
        setEnrichmentResult(company.company_research);
      }

      // Load KPIs for management section
      await loadCompanyKpis(company.id);
    } else {
      resetCompanyForm();
    }
    setCompanyDialog(true);
  };

  const openSiteDialog = (site?: CompanySite) => {
    if (site) {
      setEditingSite(site);
      setSiteForm({
        name: site.name,
        engineers: site.engineers,
        manufacturers_internal: site.manufacturers_internal,
        manufacturers_outsourced: site.manufacturers_outsourced,
        purchases: site.purchases,
        holds_inventory: site.holds_inventory,
        raw_material_part_count: site.raw_material_part_count,
        finished_good_part_count: site.finished_good_part_count,
      });
    } else {
      resetSiteForm();
    }
    setSiteDialog(true);
  };

  if (!canManageClients) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Access denied. Client management privileges required.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading company data...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <BusinessIcon fontSize="large" />
        Company Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Companies" icon={<BusinessIcon />} />
          <Tab label={`Users ${selectedCompany ? `(${companyUsers.length})` : ''}`} icon={<PeopleIcon />} disabled={!selectedCompany} />
          <Tab label={`Sites ${selectedCompany ? `(${companySites.length})` : ''}`} icon={<LocationIcon />} disabled={!selectedCompany} />
        </Tabs>

        {/* Companies Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">All Companies</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openCompanyDialog()}
            >
              Add Company
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Employees</TableCell>
                  <TableCell>Sites</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies.map((company) => (
                  <TableRow 
                    key={company.id}
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: highlightedCompanyId === company.id ? 'primary.50' : 'inherit',
                      border: highlightedCompanyId === company.id ? 2 : 0,
                      borderColor: highlightedCompanyId === company.id ? 'primary.main' : 'transparent',
                      transition: 'all 0.3s ease',
                    }}
                    onClick={() => {
                      loadCompanyDetails(company);
                      setTabValue(1); // Go to Sites tab by default
                    }}
                  >
                    <TableCell>
                      <Typography variant="subtitle2">{company.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {company.city}, {company.postcode}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={company.employee_count} 
                        size="small" 
                        color="primary" 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={company.sites_count ?? company.site_count ?? 0} 
                        size="small" 
                        color="secondary" 
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Edit Company">
                        <IconButton 
                          size="small" 
                          onClick={() => openCompanyDialog(company)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Company">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteCompany(company)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Users Tab */}
        <TabPanel value={tabValue} index={1}>
          {selectedCompany && (
            <>
              <Typography variant="h6" gutterBottom>
                {selectedCompany.name} - Users
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon />
                  Users ({companyUsers.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openUserAssignDialog}
                >
                  Assign User
                </Button>
              </Box>
              
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                {companyUsers.map((user) => (
                  <Paper key={user.id} sx={{ p: 2 }}>
                    <Typography variant="h6">
                      {user.first_name} {user.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {user.user_email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Username: {user.user_name}
                    </Typography>
                    {user.is_primary_user && (
                      <Chip label="Primary User" size="small" color="primary" />
                    )}
                  </Paper>
                ))}
                {companyUsers.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4 }}>
                    No users assigned yet. Click "Assign User" to get started.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </TabPanel>

        {/* Sites Tab */}
        <TabPanel value={tabValue} index={2}>
          {selectedCompany && (
            <>
              <Typography variant="h6" gutterBottom>
                {selectedCompany.name} - Sites
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon />
                  Sites ({companySites.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => openSiteDialog()}
                >
                  Add Site
                </Button>
              </Box>
              
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                {companySites.map((site) => (
                  <Paper key={site.id} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6">{site.name}</Typography>
                      <IconButton size="small" onClick={() => openSiteDialog(site)}>
                        <EditIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {site.engineers && <Chip label="Engineers" size="small" />}
                      {site.manufacturers_internal && <Chip label="Internal Mfg" size="small" />}
                      {site.purchases && <Chip label="Purchasing" size="small" />}
                      {site.holds_inventory && <Chip label="Inventory" size="small" />}
                    </Box>
                  </Paper>
                ))}
                {companySites.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4 }}>
                    No sites added yet. Click "Add Site" to get started.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </TabPanel>
      </Paper>

      {/* Company Dialog */}
      <Dialog open={companyDialog} onClose={() => setCompanyDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCompany ? 'Edit Company' : 'Create New Company'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Company Name and Website URL with AI Enrichment */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                variant="outlined"
                label="Company Name"
                value={companyForm.name}
                onChange={(e) => {
                  setCompanyForm({ ...companyForm, name: e.target.value });
                  if (formErrors.name) setFormErrors({ ...formErrors, name: '' });
                }}
                error={!!formErrors.name}
                helperText={formErrors.name}
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                variant="outlined"
                label="Website URL"
                placeholder="https://www.example.com"
                value={companyForm.website_url}
                onChange={(e) => setCompanyForm({ ...companyForm, website_url: e.target.value })}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <LanguageIcon sx={{ mr: 1, color: 'action.active' }} />,
                }}
                helperText="Optional - helps AI provide more accurate information"
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Tooltip title="Use AI to fetch company information from public sources">
                  <span>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleEnrichCompany}
                      disabled={enrichmentLoading || !companyForm.name.trim()}
                      sx={{ 
                        minWidth: 140, 
                        height: 56,
                        whiteSpace: 'nowrap'
                      }}
                      startIcon={enrichmentLoading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                    >
                      {enrichmentLoading ? 'Fetching...' : 'Fetch Info'}
                    </Button>
                  </span>
                </Tooltip>
                {/* Research status indicator */}
                {editingCompany?.company_research && (
                  <Typography variant="caption" color="success.main" sx={{ mt: 0.5, textAlign: 'center' }}>
                    ✓ Research available
                  </Typography>
                )}
                {editingCompany && !editingCompany.company_research && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
                    No research yet
                  </Typography>
                )}
              </Box>
            </Box>
            
            {/* Enrichment Error Alert */}
            {enrichmentError && (
              <Alert severity="error" onClose={() => setEnrichmentError(null)}>
                {enrichmentError}
              </Alert>
            )}

            {/* Industry auto-selection notice */}
            {industryAutoSelectedMessage && (
              <Alert severity="info" onClose={() => setIndustryAutoSelectedMessage(null)}>
                {industryAutoSelectedMessage}
              </Alert>
            )}
            
            {/* City field */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                label="City"
                value={companyForm.city}
                onChange={(e) => {
                  setCompanyForm({ ...companyForm, city: e.target.value });
                  if (formErrors.city) setFormErrors({ ...formErrors, city: '' });
                }}
                error={!!formErrors.city}
                helperText={formErrors.city}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                label="Postcode"
                value={companyForm.postcode}
                onChange={(e) => {
                  setCompanyForm({ ...companyForm, postcode: e.target.value });
                  if (formErrors.postcode) setFormErrors({ ...formErrors, postcode: '' });
                }}
                error={!!formErrors.postcode}
                helperText={formErrors.postcode}
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                variant="outlined"
                select
                label="Country"
                value={companyForm.country}
                onChange={(e) => {
                  setCompanyForm({ ...companyForm, country: e.target.value });
                  if (formErrors.country) setFormErrors({ ...formErrors, country: '' });
                }}
                error={!!formErrors.country}
                helperText={formErrors.country}
                required
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected) {
                      return <span style={{ color: '#999' }}>Select a country</span>;
                    }
                    const country = countries.find(c => c.id.toString() === selected);
                    return country ? country.name : '';
                  }
                }}
              >
                <MenuItem value="" disabled>
                  Select a country
                </MenuItem>
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                label="Employee Count"
                type="number"
                value={companyForm.employee_count}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 0;
                  setCompanyForm({ ...companyForm, employee_count: value });
                  if (formErrors.employee_count && value > 0) setFormErrors({ ...formErrors, employee_count: '' });
                }}
                error={!!formErrors.employee_count}
                helperText={formErrors.employee_count}
                inputProps={{ min: 1 }}
                required
              />
              <TextField
                fullWidth
                variant="outlined"
                label="Site Count"
                type="number"
                value={companyForm.site_count}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 0;
                  setCompanyForm({ ...companyForm, site_count: value });
                  if (formErrors.site_count && value > 0) setFormErrors({ ...formErrors, site_count: '' });
                }}
                error={!!formErrors.site_count}
                helperText={formErrors.site_count}
                inputProps={{ min: 1 }}
                required
              />
            </Box>

            {/* Search-first industry selection (fills Levels 1-4 automatically) */}
            <Autocomplete
              fullWidth
              options={industrySearchOptions}
              value={industrySearchValue}
              inputValue={industrySearchQuery}
              onInputChange={(_, newValue, reason) => {
                // Keep the user's original search text after selecting an option.
                // Only update the input when the user is actively typing, or explicitly clearing.
                if (reason === 'clear') {
                  setIndustrySearchQuery('');
                  return;
                }
                if (reason !== 'input') return;
                setIndustrySearchQuery(newValue);
              }}
              onChange={(_, newValue) => {
                setIndustrySearchValue(newValue);
                if (newValue) {
                  void applyIndustrySearchSelection(newValue);
                }
              }}
              getOptionLabel={(option) => option.display}
              filterOptions={(x) => x} // server-side filtering
              loading={industrySearchLoading}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2">{option.display}</Typography>
                    {option.level04.detail && (
                      <Typography variant="caption" color="text.secondary">
                        {option.level04.detail}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Industry search"
                  placeholder="Type a keyword or code (e.g. cosmetic, detergent, 1851)"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {industrySearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {/* Level 1 Industry */}
            <TextField
              fullWidth
              variant="outlined"
              select
              label="Industry Level 1"
              value={companyForm.industry_level01}
              onChange={handleLevel01Change}
              error={!!formErrors.industry_level01}
              helperText={formErrors.industry_level01}
              required
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected: unknown) => {
                  if (!selected || selected === '') {
                    return <span style={{ color: '#999' }}>Select primary industry</span>;
                  }
                  const industry = level01Industries.find(i => i.id.toString() === selected.toString());
                  return industry ? `${industry.code} - ${industry.name}` : String(selected);
                }
              }}
            >
              <MenuItem value="" disabled>
                Select primary industry
              </MenuItem>
              {level01Industries.map((industry) => (
                <MenuItem key={industry.id} value={industry.id}>
                  {industry.code} - {industry.name}
                </MenuItem>
              ))}
            </TextField>
            
            {/* Level 2 Industry */}
            <TextField
              fullWidth
              variant="outlined"
              select
              label="Industry Level 2"
              value={companyForm.industry_level02}
              onChange={handleLevel02Change}
              disabled={!companyForm.industry_level01}
              error={!!formErrors.industry_level02}
              helperText={formErrors.industry_level02}
              required
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected: unknown) => {
                  if (!selected || selected === '') {
                    return <span style={{ color: '#999' }}>Select sector</span>;
                  }
                  const industry = level02Industries.find(i => i.id.toString() === selected.toString());
                  return industry ? `${industry.code} - ${industry.name}` : String(selected);
                }
              }}
            >
              <MenuItem value="" disabled>
                Select sector
              </MenuItem>
              {level02Industries.map((industry) => (
                <MenuItem key={industry.id} value={industry.id}>
                  {industry.code} - {industry.name}
                </MenuItem>
              ))}
            </TextField>
            
            {/* Level 3 Industry */}
            <TextField
              fullWidth
              variant="outlined"
              select
              label="Industry Level 3"
              value={companyForm.industry_level03}
              onChange={handleLevel03Change}
              disabled={!companyForm.industry_level02}
              error={!!formErrors.industry_level03}
              helperText={formErrors.industry_level03}
              required
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected: unknown) => {
                  if (!selected || selected === '') {
                    return <span style={{ color: '#999' }}>Select subsector</span>;
                  }
                  const industry = level03Industries.find(i => i.id.toString() === selected.toString());
                  return industry ? `${industry.code} - ${industry.name}` : String(selected);
                }
              }}
            >
              <MenuItem value="" disabled>
                Select subsector
              </MenuItem>
              {level03Industries.map((industry) => (
                <MenuItem key={industry.id} value={industry.id}>
                  {industry.code} - {industry.name}
                </MenuItem>
              ))}
            </TextField>
            
            {/* Level 4 Industry */}
            <TextField
              fullWidth
              variant="outlined"
              select
              label="Industry Level 4"
              value={companyForm.industry_level04}
              onChange={handleLevel04Change}
              disabled={!companyForm.industry_level03}
              error={!!formErrors.industry_level04}
              helperText={formErrors.industry_level04}
              required
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected: unknown) => {
                  if (!selected || selected === '') {
                    return <span style={{ color: '#999' }}>Select specific industry</span>;
                  }
                  const industry = level04Industries.find(i => i.id.toString() === selected.toString());
                  return industry ? `${industry.code} - ${industry.name}` : String(selected);
                }
              }}
            >
              <MenuItem value="" disabled>
                Select specific industry
              </MenuItem>
              {level04Industries.map((industry) => (
                <MenuItem key={industry.id} value={industry.id}>
                  {industry.code} - {industry.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Level 4 description (Detail) */}
            {companyForm.industry_level04 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 1, display: 'block' }}>
                {level04Industries.find(i => i.id.toString() === companyForm.industry_level04)?.detail || ''}
              </Typography>
            )}
            
            {/* Company Research Results - shown after industry selection */}
            {enrichmentResult && (
              <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
                <CardContent>
                  {/* Summary Section */}
                  {enrichmentResult.structured_data && (
                    <Box sx={{ mb: 2 }}>
                      <Alert severity="success" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2">
                          ✨ Company research {editingCompany?.company_research ? 'loaded' : 'fetched successfully'}!
                        </Typography>
                      </Alert>
                      
                      {enrichmentResult.structured_data.company_description && (
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          {enrichmentResult.structured_data.company_description}
                        </Typography>
                      )}
                      
                      {/* Key Facts */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {enrichmentResult.structured_data.headquarters_city && (
                          <Chip 
                            icon={<LocationIcon />} 
                            label={`HQ: ${enrichmentResult.structured_data.headquarters_city}${enrichmentResult.structured_data.headquarters_country ? `, ${enrichmentResult.structured_data.headquarters_country}` : ''}`}
                            variant="outlined"
                            size="small"
                          />
                        )}
                        {enrichmentResult.structured_data.employee_count_estimate && (
                          <Chip 
                            icon={<PersonIcon />} 
                            label={`~${enrichmentResult.structured_data.employee_count_estimate} employees`}
                            variant="outlined"
                            size="small"
                          />
                        )}
                        {enrichmentResult.structured_data.founded_year && (
                          <Chip 
                            label={`Founded: ${enrichmentResult.structured_data.founded_year}`}
                            variant="outlined"
                            size="small"
                          />
                        )}
                        {enrichmentResult.structured_data.is_manufacturer !== null && enrichmentResult.structured_data.is_manufacturer !== undefined && (
                          <Chip 
                            label={enrichmentResult.structured_data.is_manufacturer ? 'Manufacturer' : 'Non-Manufacturer'}
                            color={enrichmentResult.structured_data.is_manufacturer ? 'primary' : 'default'}
                            variant="outlined"
                            size="small"
                          />
                        )}
                        {enrichmentResult.structured_data.holds_inventory !== null && enrichmentResult.structured_data.holds_inventory !== undefined && (
                          <Chip 
                            label={enrichmentResult.structured_data.holds_inventory ? 'Holds Inventory' : 'No Inventory'}
                            color={enrichmentResult.structured_data.holds_inventory ? 'primary' : 'default'}
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </Box>
                      
                      {/* Industry Keywords */}
                      {enrichmentResult.structured_data.industry_keywords && enrichmentResult.structured_data.industry_keywords.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            Industry Keywords:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {enrichmentResult.structured_data.industry_keywords.map((keyword, idx) => (
                              <Chip key={idx} label={keyword} size="small" />
                            ))}
                          </Box>
                        </Box>
                      )}
                      
                      {enrichmentResult.structured_data.confidence_level && (
                        <Typography variant="caption" color="text.secondary">
                          Confidence: {enrichmentResult.structured_data.confidence_level}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {/* Full Profile Toggle */}
                  {enrichmentResult.full_profile && (
                    <Box>
                      <Button
                        onClick={() => setShowFullProfile(!showFullProfile)}
                        endIcon={showFullProfile ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        size="small"
                        sx={{ mb: 1 }}
                      >
                        {showFullProfile ? 'Hide' : 'Show'} Full Research Report
                      </Button>
                      <Collapse in={showFullProfile}>
                        <Paper
                          sx={(theme) => ({
                            p: 2,
                            // Important: do NOT force a light background in dark mode, otherwise text becomes unreadable.
                            bgcolor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            border: `1px solid ${theme.palette.divider}`,
                            maxHeight: 400,
                            overflow: 'auto',
                            '& h1': {
                              fontSize: '1.25rem',
                              fontWeight: 600,
                              mt: 2,
                              mb: 1,
                              color:
                                theme.palette.mode === 'dark'
                                  ? theme.palette.primary.light
                                  : theme.palette.primary.main,
                            },
                            '& h2': {
                              fontSize: '1.1rem',
                              fontWeight: 600,
                              mt: 2,
                              mb: 1,
                              color:
                                theme.palette.mode === 'dark'
                                  ? theme.palette.primary.light
                                  : theme.palette.primary.dark,
                            },
                            '& h3': { fontSize: '1rem', fontWeight: 600, mt: 1.5, mb: 0.5 },
                            '& p': { mb: 1, lineHeight: 1.6, fontSize: '0.875rem' },
                            '& ul, & ol': { pl: 2, mb: 1 },
                            '& li': { mb: 0.25, fontSize: '0.875rem' },
                            '& strong': { fontWeight: 600 },
                          })}
                        >
                          <ReactMarkdown>
                            {enrichmentResult.full_profile}
                          </ReactMarkdown>
                        </Paper>
                      </Collapse>
                    </Box>
                  )}
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    onClick={() => setEnrichmentResult(null)}
                  >
                    Clear Results
                  </Button>
                </CardActions>
              </Card>
            )}

            {/* KPI Management Section (available after company is created) */}
            <Card sx={{ mt: 2 }} variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6">KPI Management</Typography>
                  <Button
                    size="small"
                    onClick={() => setKpiSectionExpanded(prev => !prev)}
                    endIcon={kpiSectionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  >
                    {kpiSectionExpanded ? 'Hide' : 'Show'}
                  </Button>
                </Box>

                <Collapse in={kpiSectionExpanded}>
                  {!editingCompany?.id ? (
                    <Alert severity="info">
                      Create the company first, then you can generate and edit KPIs here.
                    </Alert>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {kpiError && (
                        <Alert severity="error" onClose={() => setKpiError(null)}>
                          {kpiError}
                        </Alert>
                      )}

                      {isGeneratingKpis && (
                        <Alert severity="info" icon={<CircularProgress size={20} />}>
                          Generating KPIs in the background. This can take up to 1–2 minutes. This panel will update automatically.
                        </Alert>
                      )}

                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          startIcon={<AutoAwesomeIcon />}
                          onClick={handleGenerateKPIs}
                          disabled={kpiLoading || isGeneratingKpis}
                        >
                          {isGeneratingKpis ? 'Generating...' : 'Auto-Generate KPIs'}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={openCreateKpiDialog}
                          disabled={kpiLoading || isGeneratingKpis}
                        >
                          Add KPI
                        </Button>
                        <Button
                          variant="text"
                          onClick={() => void loadCompanyKpis(editingCompany.id)}
                          disabled={kpiLoading}
                        >
                          Refresh
                        </Button>
                      </Box>

                      {kpiLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress />
                        </Box>
                      ) : companyKpis.length === 0 ? (
                        <Alert severity="warning">
                          No KPIs yet. Use “Auto-Generate KPIs” (requires company research) or add them manually.
                        </Alert>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Unit</TableCell>
                                <TableCell>Target</TableCell>
                                <TableCell align="right">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {companyKpis.map((kpi) => (
                                <TableRow key={kpi.id} hover>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="medium">
                                      {kpi.name}
                                    </Typography>
                                    {kpi.description && (
                                      <Typography variant="caption" color="text.secondary">
                                        {kpi.description}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Chip size="small" label={kpi.unit_type} />
                                  </TableCell>
                                  <TableCell>{kpi.target_value}</TableCell>
                                  <TableCell align="right">
                                    <IconButton size="small" title="Edit KPI" onClick={() => openEditKpiDialog(kpi)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" title="Delete KPI" onClick={() => void handleDeleteKpi(kpi)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  )}
                </Collapse>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompanyDialog(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={editingCompany ? handleUpdateCompany : handleCreateCompany}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!isFormValid()}
          >
            {editingCompany ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* KPI Create/Edit Dialog (within Company modal flow) */}
      <Dialog open={kpiDialogOpen} onClose={() => setKpiDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingKpi ? 'Edit KPI' : 'Create New KPI'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="KPI Name"
              value={kpiForm.name}
              onChange={(e) => setKpiForm({ ...kpiForm, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g., On-Time Delivery Rate"
            />
            
            <TextField
              label="Description"
              value={kpiForm.description}
              onChange={(e) => setKpiForm({ ...kpiForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="What does this KPI measure?"
            />
            
            <FormControl fullWidth>
              <InputLabel>Unit Type</InputLabel>
              <Select
                value={kpiForm.unit_type}
                onChange={(e) => setKpiForm({ ...kpiForm, unit_type: e.target.value as any })}
                label="Unit Type"
              >
                <MenuItem value="percentage">Percentage (%)</MenuItem>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="currency">Currency ($)</MenuItem>
                <MenuItem value="days">Days</MenuItem>
                <MenuItem value="count">Count</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Target Value"
              value={kpiForm.target_value}
              onChange={(e) => setKpiForm({ ...kpiForm, target_value: e.target.value })}
              fullWidth
              required
              type="number"
              placeholder="e.g., 95"
            />
            
            <TextField
              label="Tolerance (%)"
              value={kpiForm.tolerance_percentage}
              onChange={(e) => setKpiForm({ ...kpiForm, tolerance_percentage: e.target.value })}
              fullWidth
              required
              type="number"
              placeholder="e.g., 10"
              helperText="Acceptable variance from target"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={kpiForm.higher_is_better}
                  onChange={(e) => setKpiForm({ ...kpiForm, higher_is_better: e.target.checked })}
                />
              }
              label="Higher is Better"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => void handleSaveKpi()}
            variant="contained"
            disabled={!kpiForm.name || !kpiForm.target_value}
          >
            {editingKpi ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Site Dialog */}
      <Dialog open={siteDialog} onClose={() => setSiteDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSite ? 'Edit Site' : 'Create New Site'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Site Name"
              value={siteForm.name}
              onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
              required
              helperText="Site name is required"
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={siteForm.engineers}
                    onChange={(e) => setSiteForm({ ...siteForm, engineers: e.target.checked })}
                  />
                }
                label="Engineers"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={siteForm.manufacturers_internal}
                    onChange={(e) => setSiteForm({ ...siteForm, manufacturers_internal: e.target.checked })}
                  />
                }
                label="Internal Manufacturing"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={siteForm.manufacturers_outsourced}
                    onChange={(e) => setSiteForm({ ...siteForm, manufacturers_outsourced: e.target.checked })}
                  />
                }
                label="Outsourced Manufacturing"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={siteForm.purchases}
                    onChange={(e) => setSiteForm({ ...siteForm, purchases: e.target.checked })}
                  />
                }
                label="Purchasing"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={siteForm.holds_inventory}
                    onChange={(e) => setSiteForm({ ...siteForm, holds_inventory: e.target.checked })}
                  />
                }
                label="Holds Inventory"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Raw Material Parts"
                type="number"
                value={siteForm.raw_material_part_count}
                onChange={(e) => setSiteForm({ ...siteForm, raw_material_part_count: parseInt(e.target.value) || 0 })}
              />
              <TextField
                fullWidth
                label="Finished Goods Parts"
                type="number"
                value={siteForm.finished_good_part_count}
                onChange={(e) => setSiteForm({ ...siteForm, finished_good_part_count: parseInt(e.target.value) || 0 })}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSiteDialog(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateSite}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            {editingSite ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Assignment Dialog */}
      <Dialog open={userAssignDialog} onClose={() => {setUserAssignDialog(false); resetUserForm();}} maxWidth="md" fullWidth>
        <DialogTitle>
          {isAddMode ? 'Add New User' : 'Assign Existing User'} to {selectedCompany?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Mode Toggle */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant={!isAddMode ? "contained" : "outlined"}
                onClick={() => setIsAddMode(false)}
                fullWidth
              >
                Assign Existing User
              </Button>
              <Button
                variant={isAddMode ? "contained" : "outlined"}
                onClick={() => setIsAddMode(true)}
                fullWidth
              >
                Add New User
              </Button>
            </Box>

            {!isAddMode ? (
              /* Assign Existing User */
              <>
                <FormControl fullWidth>
                  <InputLabel>Select User</InputLabel>
                  <Select
                    value={selectedUserId || ''}
                    label="Select User"
                    onChange={(e) => setSelectedUserId(Number(e.target.value))}
                  >
                    {allUsers.map(user => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.username}) - {user.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {allUsers.length === 0 && (
                  <Alert severity="info">
                    No unassigned users available. Use "Add New User" to create a new user.
                  </Alert>
                )}
              </>
            ) : (
              /* Add New User - Invite Flow */
              <>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={newUserForm.first_name}
                    onChange={(e) => setNewUserForm({...newUserForm, first_name: e.target.value})}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={newUserForm.last_name}
                    onChange={(e) => setNewUserForm({...newUserForm, last_name: e.target.value})}
                    required
                  />
                </Box>
                
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})}
                  required
                  helperText="An invitation email will be sent to this address"
                />
                
                <Alert severity="info" sx={{ mt: 1 }}>
                  The user will receive an email invitation to set up their account and choose their own password.
                </Alert>
              </>
            )}
            
            {/* Primary User Toggle - Available for both modes */}
            <FormControlLabel
              control={
                <Switch
                  checked={isPrimaryUser}
                  onChange={(e) => setIsPrimaryUser(e.target.checked)}
                  color="primary"
                />
              }
              label="Set as Primary User"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {setUserAssignDialog(false); resetUserForm();}}>
            Cancel
          </Button>
          <Button 
            onClick={isAddMode ? handleCreateAndAssignUser : handleAssignUser} 
            variant="contained"
            disabled={isAddMode ? 
              (!newUserForm.email || !newUserForm.first_name || !newUserForm.last_name) :
              !selectedUserId
            }
          >
            {isAddMode ? 'Send Invitation' : 'Assign User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CompanyManagement; 